import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { SessionService } from "@recore-solutions/nstar-lib";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { ConfigurationService } from "src/app/configuration.service";
import { PermissionListGuard } from "src/app/permission-list.guard";
import { PreferencesService } from "src/app/preferences/preferences.service";
import { PermissionToken } from "src/app/shared/interfaces/listing-permission";
import { ListingSummary } from "src/app/shared/interfaces/listing-summary";
import * as LISTING_CONSTANTS from "src/app/listing/listing-constants";

@Component({
	selector: "app-listing-result",
	templateUrl: "./listing-result.component.html",
	styleUrls: ["./listing-result.component.scss"],
})
export class ListingResultComponent implements OnInit {
	@Input() listing: ListingSummary;
	@Input() showPreviewAd: boolean;
	@Input() hasPreviewPermissions: boolean;
	@Output() deleted = new EventEmitter<ListingSummary>();
	@Output() recovered = new EventEmitter<ListingSummary>();
	@Output() copied = new EventEmitter<ListingSummary>();
	@Output() cloned = new EventEmitter<ListingSummary>();
	@Output() navigate = new EventEmitter<ListingSummary>();
	@Output() previewAdEvent = new EventEmitter<any>();

	listingDisplayAgentProp$: Observable<string>;
	displayName$: Observable<string>;
	tooltipName$: Observable<string>;
	listingDisplayLocationProp$: Observable<string>;
	displayLocationName$: Observable<string>;
	tooltipLocationName$: Observable<string>;
	adbuilderButtonTitle = LISTING_CONSTANTS.ADBUILDER_BUTTON_TITLE;

	errorLoadingImage = false;

	canCopy$: Observable<boolean>;
	canClone$: Observable<boolean>;
	canDelete$: Observable<boolean>;
	canRecover$: Observable<boolean>;

	get showBuyerMember(): boolean {
		return this.listing?.listingStatusCD === "COMPS" || this.listing?.listingStatusCD === "OAS";
	}

	get memberFullName(): string {
		return this.showBuyerMember
			? this.listing?.buyerMemberFullName
			: this.listing?.listingMemberFullName;
	}

	get memberReference(): string {
		return this.showBuyerMember
			? this.listing?.buyerMemberReference
			: this.listing?.listingMemberReference;
	}

	get price(): number {
		return this.listing?.salesPrice || this.listing?.listPrice || this.listing?.price;
	}

	constructor(
		protected router: Router,
		protected configurationService: ConfigurationService,
		protected preferencesService: PreferencesService,
		protected sessionService: SessionService,
		protected permissionListGuard: PermissionListGuard
	) {
		this.listingDisplayAgentProp$ = this.preferencesService
			.getPreference(["homepage", "listingDisplayAgent"])
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));
		this.displayName$ = this.listingDisplayAgentProp$.pipe(
			map((type) => (type === "Agent Name" ? this.memberFullName : this.memberReference))
		);
		this.tooltipName$ = this.listingDisplayAgentProp$.pipe(
			map((type) => (type === "Agent Name" ? this.memberReference : this.memberFullName))
		);
		this.listingDisplayLocationProp$ = this.preferencesService
			.getPreference(["homepage", "listingDisplayLocation"])
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));
		this.displayLocationName$ = this.listingDisplayLocationProp$.pipe(
			map((prop) => (prop === "County" ? this.listing.countyName : this.listing.cityName))
		);
		this.tooltipLocationName$ = this.listingDisplayLocationProp$.pipe(
			map((prop) => (prop === "County" ? this.listing.cityName : this.listing.countyName))
		);
	}

	ngOnInit(): void {
		this.canCopy$ = this.permissionListGuard.listingHasPermissionToken(
			this.listing.listingID,
			"copy:listing" as PermissionToken
		);
		this.canClone$ = this.permissionListGuard.listingHasPermissionToken(
			this.listing.listingID,
			"clone:listing" as PermissionToken
		);
		this.canDelete$ = this.permissionListGuard.listingHasPermissionToken(
			this.listing.listingID,
			"delete:listing" as PermissionToken
		);
		this.canRecover$ = this.permissionListGuard.listingHasPermissionToken(
			this.listing.listingID,
			"recover:listing" as PermissionToken
		);
	}

	get photoUrl(): string {
		return this.errorLoadingImage
			? null
			: this.listing.mediaFileName + "/thumb?t=" + this.listing.modificationTimestampUTCDTTM;
	}

	get isDeleted(): boolean {
		return (
			!!this.listing &&
			!!this.listing.listingStatusCD &&
			["id", "wd", "d"].indexOf(this.listing.listingStatusCD.toLowerCase()) > -1
		);
	}

	get showExpiresBanner(): boolean {
		return this.showExpiringIn || this.showExpiredOn;
	}

	get showExpiringIn(): boolean {
		const expireCountDownStatuses = ["W", "ACT", "TNAFS", "I"];
		return (
			expireCountDownStatuses.indexOf(this.listing.listingStatusCD) > -1 &&
			this.expiresIn !== null &&
			this.expiresIn <= this.configurationService.config.listings.expirations.incomplete &&
			this.expiresIn >= 0
		);
	}

	get showExpiredOn(): boolean {
		return this.listing.listingStatusCD === "EXP" && this.listing.expirationDT !== null;
	}

	get expiresIn(): number {
		return this.listing.daysUntilExpiration;
	}

	get hasContingency(): boolean {
		return this.listing.contingencyTypeCD && this.listing.contingencyTypeCD !== "N";
	}

	deleteListing(event: any): void {
		this.deleted.emit(this.listing);
		event.stopPropagation();
	}

	recoverListing(event: any): void {
		this.recovered.emit(this.listing);
		event.stopPropagation();
	}

	copyListing(event: any): void {
		this.copied.emit(this.listing);
		event.stopPropagation();
	}

	cloneListing(event: any): void {
		this.cloned.emit(this.listing);
		event.stopPropagation();
	}

	preventDefault($event: MouseEvent): void {
		$event.stopPropagation();
	}

	navigateListing($event: KeyboardEvent): void {
		$event.preventDefault();
		this.navigate.emit(this.listing);
	}

	previewAd($event: MouseEvent) {
		$event.preventDefault();
		$event.stopPropagation();
		this.previewAdEvent.emit(this.listing);
	}
}
